import {
  GUIA_E, GUIA_NAO_E, URGENCIA, AVALIACAO, LEVE_OBSERVACOES,
  USE_ASSIM, EVITE_ASSIM, REGRA_DE_OURO,
  AUTOCUIDADO_RESPONSABILIDADE, CICLO_COMPASSO, SAFETY_CLOSING,
  EVALUATION_SOURCE, USAGE_SOURCE,
} from "@/data/safety";
import { PageHeader, SourceTag } from "@/components/common";
import {
  TriangleAlert, CalendarClock, ClipboardList, Check, X, ShieldCheck,
  Eye, FileText, MessageCircle, Stethoscope, HeartPulse, Compass,
} from "lucide-react";

const CARE_ICONS = [Eye, FileText, MessageCircle, Stethoscope, HeartPulse];

const List = ({ items, icon: Icon, tone }) => (
  <ul className="mt-3 space-y-2">
    {items.map((it, i) => (
      <li key={i} className="flex items-start gap-2 text-sm text-foreground">
        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone}`} strokeWidth={2} /> {it}
      </li>
    ))}
  </ul>
);

export default function Safety() {
  return (
    <div className="space-y-12">
      {/* SEÇÃO 1: AVALIAÇÃO PROFISSIONAL (Pág. impressa 47 / física 49) */}
      <section className="space-y-8">
        <PageHeader
          eyebrow="Uso seguro do guia"
          title="Quando procurar avaliação profissional"
          subtitle="Autocuidado consciente também sabe pedir ajuda. Este guia é educativo: ajuda você a observar sinais e hábitos, mas não substitui diagnóstico, tratamento ou acompanhamento de profissionais de saúde."
        >
          <div className="mt-3">
            <SourceTag source={EVALUATION_SOURCE} />
          </div>
        </PageHeader>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="rounded-3xl border border-destructive/30 bg-destructive/5 p-7" data-testid="safety-urgencia">
            <div className="flex items-center gap-2.5">
              <TriangleAlert className="h-6 w-6 text-destructive" />
              <h3 className="font-serif text-xl font-medium text-foreground">Procure ajuda com urgência</h3>
            </div>
            <List items={URGENCIA} icon={TriangleAlert} tone="text-destructive" />
          </div>

          <div className="rounded-3xl border border-border bg-card p-7" data-testid="safety-avaliacao">
            <div className="flex items-center gap-2.5">
              <CalendarClock className="h-6 w-6 text-gold" />
              <h3 className="font-serif text-xl font-medium text-foreground">Marque uma avaliação</h3>
            </div>
            <List items={AVALIACAO} icon={Check} tone="text-gold" />
          </div>

          <div className="rounded-3xl border border-border bg-card p-7" data-testid="safety-observacoes">
            <div className="flex items-center gap-2.5">
              <ClipboardList className="h-6 w-6 text-primary" />
              <h3 className="font-serif text-xl font-medium text-foreground">Leve suas observações</h3>
            </div>
            <List items={LEVE_OBSERVACOES} icon={Check} tone="text-primary" />
          </div>
        </div>

        {/* Autocuidado com responsabilidade */}
        <div className="rounded-3xl border border-border bg-card p-6 sm:p-8">
          <h4 className="font-serif text-xl font-medium text-foreground text-center sm:text-left">
            Autocuidado com responsabilidade
          </h4>
          <div className="mt-6 grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {AUTOCUIDADO_RESPONSABILIDADE.map((passo, i) => {
              const Icon = CARE_ICONS[i] || Eye;
              return (
                <div key={i} className="flex flex-col items-center sm:items-start text-center sm:text-left rounded-2xl bg-secondary/40 p-4">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gold/20 text-gold text-xs font-semibold mb-2">
                    <Icon className="h-4 w-4" />
                  </span>
                  <p className="text-sm font-medium text-foreground">{passo}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-secondary/50 p-5 text-center">
          <p className="text-sm italic text-muted-foreground leading-relaxed">
            “{SAFETY_CLOSING.fraseResponsabilidade}”
          </p>
        </div>
      </section>

      {/* SEÇÃO 2: COMO USAR ESTE GUIA COM SEGURANÇA (Pág. impressa 48 / física 50) */}
      <section className="space-y-8 border-t border-border pt-10">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-foreground">
              Como usar este guia com segurança
            </h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl leading-relaxed">
              O objetivo deste material é apoiar sua observação corporal e inspirar escolhas simples de autocuidado. Use-o como uma bússola educativa, não como uma regra rígida.
            </p>
          </div>
          <SourceTag source={USAGE_SOURCE} />
        </div>

        {/* Bússola do ciclo de cuidado */}
        <div className="rounded-3xl border border-gold/30 bg-card p-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-gold/15 px-4 py-1.5 text-xs font-medium text-gold mb-4">
            <Compass className="h-4 w-4" /> O ciclo da observação
          </div>
          <div className="flex flex-wrap justify-center gap-3 sm:gap-6">
            {CICLO_COMPASSO.map((etapa, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary font-serif text-sm font-semibold text-foreground">
                  {i + 1}
                </span>
                <span className="text-sm font-medium text-foreground">{etapa}</span>
                {i < CICLO_COMPASSO.length - 1 && <span className="text-muted-foreground ml-2 hidden sm:inline">→</span>}
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-3xl border border-border bg-card p-7">
            <h3 className="font-serif text-xl font-medium text-foreground">Use assim</h3>
            <List items={USE_ASSIM} icon={Check} tone="text-primary" />
          </div>
          <div className="rounded-3xl border border-border bg-card p-7">
            <h3 className="font-serif text-xl font-medium text-foreground">Evite assim</h3>
            <List items={EVITE_ASSIM} icon={X} tone="text-destructive" />
          </div>
        </div>

        {/* A Regra de Ouro */}
        <div className="flex items-start gap-4 rounded-3xl border border-gold/30 bg-gold/10 p-8">
          <ShieldCheck className="mt-1 h-7 w-7 shrink-0 text-gold" />
          <div>
            <h3 className="font-serif text-2xl font-medium text-foreground">A regra de ouro</h3>
            <p className="mt-2 text-base font-medium leading-relaxed text-foreground">{REGRA_DE_OURO}</p>
            <p className="mt-2 text-sm italic text-muted-foreground">
              {SAFETY_CLOSING.fraseSeguranca}
            </p>
          </div>
        </div>
      </section>

      {/* SEÇÃO 3: IDENTIDADE E PROPÓSITO DO GUIA (Pág. 4 / impressa 2) */}
      <section className="space-y-6 border-t border-border pt-10">
        <h3 className="font-serif text-xl font-medium text-foreground">Propósito e limites do material</h3>
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-3xl border border-border bg-card p-7">
            <h4 className="font-serif text-lg font-medium text-primary">Este guia É</h4>
            <List items={GUIA_E} icon={Check} tone="text-primary" />
          </div>
          <div className="rounded-3xl border border-border bg-card p-7">
            <h4 className="font-serif text-lg font-medium text-destructive">Este guia NÃO é</h4>
            <List items={GUIA_NAO_E} icon={X} tone="text-destructive" />
          </div>
        </div>
      </section>
    </div>
  );
}
